import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Drop, Package, Calendar, Users, Trash, ArrowRight, WarningCircle, Info, Plus,
} from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CYCLE_TONE = {
  Daily:   { bg: '#EEF6FF', text: '#1E3A8A', border: '#DBEAFE' },
  Weekly:  { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
  Monthly: { bg: '#DCFCE7', text: '#166534', border: '#BBF7D0' },
};

const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString();

const StatTile = ({ label, value, muted = false }) => (
  <div className="bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg px-4 py-3">
    <div className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</div>
    <div className={`mt-1 text-lg font-bold ${muted ? 'text-[#78716C]' : 'text-[#0A2540]'}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</div>
  </div>
);

export function BundlesTab() {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [entityCurrency, setEntityCurrency] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(true);

  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyForm = { bundle_name: '', cycle: 'Daily', amount: '', litres: '', daily_limit: '0', weekly_limit: '0' };
  const [form, setForm] = useState(emptyForm);

  // Load entities
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/entities`, { withCredentials: true, headers: authHeaders() });
        const rows = Array.isArray(data) ? data : (data?.entities || []);
        setEntities(rows);
        if (rows.length && !entityId) setEntityId(String(rows[0].id || rows[0].entity_id || rows[0]._id || ''));
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load entities'));
      } finally {
        setLoadingEntities(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track currency of selected entity for display
  useEffect(() => {
    const ent = entities.find(e => String(e.id || e.entity_id || e._id) === String(entityId));
    setEntityCurrency(ent?.currency || '');
  }, [entityId, entities]);

  const fetchBundles = useCallback(async () => {
    if (!entityId) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/entities/${entityId}/bundles`, { withCredentials: true, headers: authHeaders() });
      setBundles(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(formatApiError(e, 'Failed to load bundles'));
      setBundles([]);
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  useEffect(() => { fetchBundles(); }, [fetchBundles]);

  const activeBundles = useMemo(() => bundles.filter(b => (b.status || '').toLowerCase() === 'active'), [bundles]);

  const openDetail = async (bundle) => {
    setSelected(bundle);
    setDetailOpen(true);
    setStats(null);
    setStatsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/entities/${entityId}/bundles/${bundle.id}/stats`,
        { withCredentials: true, headers: authHeaders() },
      );
      setStats(data);
    } catch {
      setStats({ available: false, message: 'Stats endpoint unavailable.' });
    } finally {
      setStatsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/entities/${entityId}/bundles/${selected.id}`,
        { withCredentials: true, headers: authHeaders() },
      );
      toast.success(`Bundle "${selected.bundle_name}" deleted`);
      setDeleteConfirm(false); setDetailOpen(false);
      fetchBundles();
    } catch (e) {
      toast.error(formatApiError(e, 'Delete failed'));
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => { setForm(emptyForm); setCreateOpen(true); };

  const submitCreate = async () => {
    // Basic validation
    if (!form.bundle_name.trim()) { toast.error('Bundle name is required'); return; }
    if (!['Daily', 'Weekly', 'Monthly'].includes(form.cycle)) { toast.error('Invalid cycle'); return; }
    const amt = Number(form.amount);
    const litres = Number(form.litres);
    if (!(amt >= 0)) { toast.error('Amount must be a positive number'); return; }
    if (!(litres >= 1)) { toast.error('Litres must be at least 1'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/entities/${entityId}/bundles`,
        {
          bundle_name: form.bundle_name.trim(),
          cycle: form.cycle,
          amount: amt,
          litres: litres,
          daily_limit: Number(form.daily_limit) || 0,
          weekly_limit: Number(form.weekly_limit) || 0,
        },
        { withCredentials: true, headers: authHeaders() },
      );
      toast.success(data?.message || 'Bundle created');
      setCreateOpen(false);
      fetchBundles();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to create bundle'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="hydration-bundles-tab">
      {/* Header row */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex-1 min-w-[220px] max-w-md">
          <label className="text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Entity
          </label>
          <Select value={entityId} onValueChange={setEntityId} disabled={loadingEntities}>
            <SelectTrigger className="mt-1 border-[#EDE6D8] rounded-lg" data-testid="bundles-entity-select">
              <SelectValue placeholder={loadingEntities ? 'Loading…' : 'Select entity'} />
            </SelectTrigger>
            <SelectContent>
              {entities.map(e => {
                const id = String(e.id || e.entity_id || e._id);
                return (
                  <SelectItem key={id} value={id} data-testid={`bundles-entity-option-${id}`}>
                    {e.entity_name || e.name || `Entity ${id}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-[#78716C]" data-testid="bundles-count">
            {loading ? 'Loading…' : `${activeBundles.length} active bundle${activeBundles.length === 1 ? '' : 's'}`}
          </div>
          <Button
            onClick={openCreate}
            disabled={!entityId}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="bundle-create-btn"
          >
            <Plus size={14} className="mr-2" weight="bold" />
            New Bundle
          </Button>
        </div>
      </div>

      {/* Bundles list */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-32 rounded-lg bg-[#FCFAF6] border border-[#EDE6D8] animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="p-6 bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-sm text-[#991B1B] flex gap-2" data-testid="bundles-error">
          <WarningCircle size={18} weight="fill" />
          <span>{error}</span>
        </div>
      ) : activeBundles.length === 0 ? (
        <div className="p-10 bg-white border border-[#EDE6D8] rounded-lg text-center" data-testid="bundles-empty">
          <Package size={28} className="mx-auto text-[#78716C] mb-2" />
          <div className="text-sm font-medium text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>No active bundles</div>
          <div className="text-xs text-[#78716C] mt-1">This entity hasn&apos;t published any hydration bundles yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="bundles-grid">
          {activeBundles.map(b => {
            const tone = CYCLE_TONE[b.cycle] || CYCLE_TONE.Monthly;
            return (
              <button
                key={b.id}
                onClick={() => openDetail(b)}
                className="text-left bg-white border border-[#EDE6D8] rounded-lg p-4 hover:border-[#0A2540] hover:shadow-sm transition-all group"
                data-testid={`bundle-card-${b.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="text-base font-bold text-[#0A2540] truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{b.bundle_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#78716C] mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ID · {b.id}</div>
                  </div>
                  <Badge
                    style={{ backgroundColor: tone.bg, color: tone.text, borderColor: tone.border }}
                    className="border text-[10px] font-semibold hover:opacity-100"
                  >
                    {b.cycle}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Amount</div>
                    <div className="text-sm font-bold text-[#0A2540]">{fmtMoney(b.amount)}{entityCurrency ? ` ${entityCurrency}` : ''}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Litres</div>
                    <div className="text-sm font-bold text-[#0A2540] flex items-center gap-1">
                      <Drop size={13} className="text-[#0A66C2]" /> {fmtInt(b.litres)} L
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs pt-3 border-t border-[#EDE6D8]">
                  <div className="text-[#78716C]">
                    {b.daily_limit > 0 && <span>Daily cap: <span className="font-semibold text-[#0A2540]">{b.daily_limit}</span></span>}
                    {b.daily_limit === 0 && b.weekly_limit === 0 && <span className="text-[#78716C]">No caps</span>}
                    {b.weekly_limit > 0 && <span className="ml-2">Weekly cap: <span className="font-semibold text-[#0A2540]">{b.weekly_limit}</span></span>}
                  </div>
                  <div className="text-[#0A2540] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    View <ArrowRight size={12} weight="bold" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="bundle-detail-dialog">
          {selected && (
            <>
              <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#EDE6D8]">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#EEF6FF] flex items-center justify-center">
                    <Package size={22} weight="fill" className="text-[#0A2540]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {selected.bundle_name}
                    </DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-1 text-xs">
                      <Badge className="bg-[#EEF6FF] text-[#1E3A8A] hover:bg-[#EEF6FF] border-0">{selected.cycle}</Badge>
                      <span className="text-[#78716C]">Bundle ID · {selected.id}</span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="px-6 py-5 max-h-[65vh] overflow-y-auto space-y-5">
                {/* Pricing & quota */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Pricing & Quota</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile label="Amount" value={`${fmtMoney(selected.amount)}${entityCurrency ? ` ${entityCurrency}` : ''}`} />
                    <StatTile label="Litres" value={`${fmtInt(selected.litres)} L`} />
                    <StatTile label="Daily Limit" value={selected.daily_limit > 0 ? fmtInt(selected.daily_limit) : 'None'} muted={selected.daily_limit === 0} />
                    <StatTile label="Weekly Limit" value={selected.weekly_limit > 0 ? fmtInt(selected.weekly_limit) : 'None'} muted={selected.weekly_limit === 0} />
                  </div>
                </div>

                {/* Subscription stats */}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#78716C] font-semibold mb-2 flex items-center gap-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <Users size={12} /> Subscription Stats
                  </div>
                  {statsLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[0, 1, 2, 3].map(i => <div key={i} className="h-16 rounded-lg bg-[#FCFAF6] animate-pulse" />)}
                    </div>
                  ) : stats?.available ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <StatTile label="Active Subs" value={fmtInt(stats.active_subscribers)} />
                      <StatTile label="Total Ever" value={fmtInt(stats.total_ever_subscribed)} />
                      <StatTile label="30d Revenue" value={`${fmtMoney(stats.last_30d_revenue)}${entityCurrency ? ` ${entityCurrency}` : ''}`} />
                      <StatTile label="30d Litres Served" value={`${fmtInt(stats.last_30d_litres_served)} L`} />
                    </div>
                  ) : (
                    <div className="p-4 bg-[#FCFAF6] border border-dashed border-[#EDE6D8] rounded-lg flex items-start gap-2 text-xs text-[#78716C]" data-testid="bundle-stats-placeholder">
                      <Info size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{stats?.message || 'Subscription stats will appear here once the middleware endpoint is published.'}</span>
                    </div>
                  )}
                </div>

                {/* Meta */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-[#FCFAF6] rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Created by</div>
                    <div className="text-sm text-[#0A2540] mt-0.5 font-mono">{selected.maker_id || '—'}</div>
                  </div>
                  <div className="p-3 bg-[#FCFAF6] rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold flex items-center gap-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <Calendar size={11} /> Last Updated
                    </div>
                    <div className="text-sm text-[#0A2540] mt-0.5">{selected.date_updated || selected.date_registered || '—'}</div>
                  </div>
                </div>
              </div>

              <DialogFooter className="px-6 py-4 border-t border-[#EDE6D8] bg-white">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(true)}
                  className="border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] rounded-lg mr-auto"
                  data-testid="bundle-delete-btn"
                >
                  <Trash size={14} className="mr-2" />
                  Delete Bundle
                </Button>
                <Button
                  onClick={() => setDetailOpen(false)}
                  className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
                  data-testid="bundle-detail-close"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <DialogContent data-testid="bundle-delete-confirm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WarningCircle size={20} weight="fill" className="text-[#EF4444]" />
              Delete bundle?
            </DialogTitle>
            <DialogDescription>
              {selected && (
                <>You are about to delete <span className="font-semibold text-[#0A2540]">{selected.bundle_name}</span>. This cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(false)} className="rounded-lg" data-testid="bundle-delete-cancel">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-lg"
              data-testid="bundle-delete-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Delete bundle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create bundle */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="bundle-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={20} weight="fill" className="text-[#0A2540]" />
              Create hydration bundle
            </DialogTitle>
            <DialogDescription>
              Bundle will be published to <span className="font-semibold">{entities.find(e => String(e.id || e.entity_id || e._id) === String(entityId))?.entity_name || '—'}</span> immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Bundle name <span className="text-[#EF4444]">*</span></Label>
              <Input
                value={form.bundle_name}
                onChange={(e) => setForm({ ...form, bundle_name: e.target.value })}
                placeholder="e.g. Monthly Streak"
                className="border-[#EDE6D8] rounded-lg"
                data-testid="bundle-create-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cycle <span className="text-[#EF4444]">*</span></Label>
              <Select value={form.cycle} onValueChange={(v) => setForm({ ...form, cycle: v })}>
                <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="bundle-create-cycle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Daily">Daily</SelectItem>
                  <SelectItem value="Weekly">Weekly</SelectItem>
                  <SelectItem value="Monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount {entityCurrency ? `(${entityCurrency})` : ''} <span className="text-[#EF4444]">*</span></Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="16200.00"
                  className="border-[#EDE6D8] rounded-lg"
                  data-testid="bundle-create-amount"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Litres <span className="text-[#EF4444]">*</span></Label>
                <Input
                  type="number" min="1" step="1"
                  value={form.litres}
                  onChange={(e) => setForm({ ...form, litres: e.target.value })}
                  placeholder="90"
                  className="border-[#EDE6D8] rounded-lg"
                  data-testid="bundle-create-litres"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Daily limit</Label>
                <Input
                  type="number" min="0" step="1"
                  value={form.daily_limit}
                  onChange={(e) => setForm({ ...form, daily_limit: e.target.value })}
                  className="border-[#EDE6D8] rounded-lg"
                  data-testid="bundle-create-daily-limit"
                />
                <p className="text-[10px] text-[#78716C]">0 = no daily cap</p>
              </div>
              <div className="space-y-1.5">
                <Label>Weekly limit</Label>
                <Input
                  type="number" min="0" step="1"
                  value={form.weekly_limit}
                  onChange={(e) => setForm({ ...form, weekly_limit: e.target.value })}
                  className="border-[#EDE6D8] rounded-lg"
                  data-testid="bundle-create-weekly-limit"
                />
                <p className="text-[10px] text-[#78716C]">0 = no weekly cap</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-lg" data-testid="bundle-create-cancel">
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={creating}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="bundle-create-submit"
            >
              <Plus size={14} className="mr-2" weight="bold" />
              {creating ? 'Creating…' : 'Create bundle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
